/* eslint-disable @typescript-eslint/no-explicit-any */
import apiClient, { IresponseData, IresponseDataPaginated } from "@/lib/api";

export enum ECollaboratorStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  SUSPENDED = "SUSPENDED",
}

export enum ECollaboratorRole {
  OWNER = "OWNER",
  SELLER = "SELLER",
}

export interface Collaborator {
  id: number;
  email: string;
  userName: string;
  firstName: string;
  lastName: string;
  status: ECollaboratorStatus;
  role: ECollaboratorRole;
  createdAt: string;
}

export interface CreateCollaboratorRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role?: ECollaboratorRole;
}

export interface UpdateCollaboratorRequest {
  email?: string;
  password?: string;
  firstName?: string;
  lastName?: string;
  role?: ECollaboratorRole;
  status?: ECollaboratorStatus;
}

export interface CollaboratorFilters {
  search?: string;
  status?: ECollaboratorStatus;
  role?: ECollaboratorRole;
  page?: number;
  limit?: number;
}

function removeEmptyFields<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {} as any);
}

export const collaboratorService = {
  async create(
    data: CreateCollaboratorRequest,
  ): Promise<IresponseData<Collaborator>> {
    try {
      const cleanData = removeEmptyFields(data);
      const response = await apiClient.post<IresponseData<Collaborator>>(
        "/collaborators",
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al registrar el colaborador. Por favor, intenta de nuevo.",
      );
    }
  },

  async getAll(
    filters?: CollaboratorFilters,
  ): Promise<IresponseDataPaginated<Collaborator[]>> {
    try {
      const cleanFilters = filters ? removeEmptyFields(filters) : {};
      const response = await apiClient.get<
        IresponseDataPaginated<Collaborator[]>
      >("/collaborators", { params: cleanFilters });
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener los colaboradores. Por favor, intenta de nuevo.",
      );
    }
  },

  async getOne(id: number): Promise<IresponseData<Collaborator>> {
    try {
      const response = await apiClient.get<IresponseData<Collaborator>>(
        `/collaborators/${id}`,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al obtener el colaborador. Por favor, intenta de nuevo.",
      );
    }
  },

  async update(
    id: number,
    data: UpdateCollaboratorRequest,
  ): Promise<IresponseData<Collaborator>> {
    try {
      const cleanData = removeEmptyFields(data);
      const response = await apiClient.put<IresponseData<Collaborator>>(
        `/collaborators/${id}`,
        cleanData,
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message ||
          "Error al actualizar el colaborador. Por favor, intenta de nuevo.",
      );
    }
  },
};
